import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { DecimalPipe } from '@angular/common';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { NgSelectModule } from '@ng-select/ng-select';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { Subscription } from 'rxjs';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { salesTax } from 'src/app/shared/models/accounting';

@Component({
    selector: 'app-accounting-options',
    templateUrl: './accounting-options.component.html',
    styleUrl: './accounting-options.component.scss',
    standalone: true,
    imports: [ReactiveFormsModule, FormsModule, DecimalPipe, CollapseModule, NgSelectModule, TooltipModule, SortableModule]
})
export class AccountingOptionsComponent implements OnInit, OnDestroy {
  inventory_cost_method_options: string[] = ['LIFO', 'FIFO', 'Average'];
  accounting_basis: string[] = ['Cash Basis'] // , 'Accrual Basis' - Accrual basis will be added in the future once we have the necessary features to support it such as accounts payable and accounts receivable management
  selected_costing_method_for_inventory;
  accountingOptionsForm: FormGroup;
  private subscription: Subscription;

  /** True once a costing method has been saved — locks the dropdown */
  get costingMethodLocked(): boolean {
    return !!this.sharedService.company_info?.accounting_options?.costing_method_for_inventory;
  }

  /** True once the user has selected a method (enables the rest of the form) */
  get otherSectionsEnabled(): boolean {
    return !!this.accountingOptionsForm.get('costing_method_for_inventory')?.value;
  }

  /** Built-in accounts shown read-only */
  get defaultAccounts() {
    return this.accountingService.income_expense_accounts.filter(
      (a) => !this.customAccounts.some(
        (c) => c.type === a.type && c.name.toLowerCase() === a.name.toLowerCase()
      )
    );
  }
  /** Defaults split by type — convenience getters for the template iteration. */
  get defaultIncomeAccounts() {
    return this.defaultAccounts.filter((a) => a.type === 'Income');
  }
  get defaultExpenseAccounts() {
    return this.defaultAccounts.filter((a) => a.type === 'Expense');
  }

  /** User-defined accounts split into separate arrays per type so each column
   * can be independently sortable via bs-sortable. The legacy combined-shape
   * `customAccounts` is exposed as a getter for the duplicate check, the
   * default-suppression filter above, and the save payload. */
  customIncomeAccounts: string[] = [];
  customExpenseAccounts: string[] = [];

  get customAccounts(): { type: 'Income' | 'Expense'; name: string }[] {
    return [
      ...this.customIncomeAccounts.map((name) => ({ type: 'Income' as const, name })),
      ...this.customExpenseAccounts.map((name) => ({ type: 'Expense' as const, name })),
    ];
  }

  accountTypeOptions: Array<'Income' | 'Expense'> = ['Income', 'Expense'];
  newAccountType: 'Income' | 'Expense' = 'Income';
  newAccountName: string = '';

  showGeneralOptions = true;
  showReceiptOptions = true;
  showChartOfAccounts = true;
  showSalesTaxRates = true;
  showPaymentOptions = true;

  // --- Payment Accounts / Methods (editable lists, persisted on company) ---
  paymentAccounts: string[] = [];
  paymentMethods: string[] = [];
  /** Unified "type + name" entry form, mirroring the Chart of Accounts UX. */
  paymentTypeOptions: Array<'Payment Account' | 'Payment Method'> = ['Payment Account', 'Payment Method'];
  newPaymentType: 'Payment Account' | 'Payment Method' = 'Payment Account';
  newPaymentName: string = '';

  addPaymentEntry() {
    const name = this.newPaymentName?.trim();
    if (!name) return;
    const defaults =
      this.newPaymentType === 'Payment Account'
        ? this.accountingService.payment_account_defaults
        : this.accountingService.payment_method_defaults;
    const customs =
      this.newPaymentType === 'Payment Account'
        ? this.paymentAccounts
        : this.paymentMethods;
    if ([...defaults, ...customs].some((e) => e.toLowerCase() === name.toLowerCase())) {
      this.sharedService.raiseWarningToast(
        `"${name}" is already in the ${this.newPaymentType} list.`,
      );
      return;
    }
    if (this.newPaymentType === 'Payment Account') {
      this.paymentAccounts = [...this.paymentAccounts, name];
    } else {
      this.paymentMethods = [...this.paymentMethods, name];
    }
    this.newPaymentName = '';
  }

  // Remove by value, not index. bs-sortable's itemTemplate context exposes
  // `item` (with `value`) but not the array index, so index-based removal
  // silently no-ops.
  removePaymentAccount(value: string) {
    this.paymentAccounts = this.paymentAccounts.filter((v) => v !== value);
  }

  removePaymentMethod(value: string) {
    this.paymentMethods = this.paymentMethods.filter((v) => v !== value);
  }

  // --- Sales Tax ---
  salesTaxOptions: salesTax[] = [];
  editingTaxIndex: number | null = null;
  newTax: { name: string; state_rate: number; county_rate: number; city_rate: number; special_rate: number } = {
    name: '', state_rate: 0, county_rate: 0, city_rate: 0, special_rate: 0
  };

  get newTaxCombined(): number {
    return (this.newTax.state_rate || 0) + (this.newTax.county_rate || 0)
         + (this.newTax.city_rate || 0) + (this.newTax.special_rate || 0);
  }

  addTaxRate() {
    const name = this.newTax.name?.trim();
    if (!name) return;
    if (this.salesTaxOptions.some(t => t.name.toLowerCase() === name.toLowerCase())) {
      this.sharedService.raiseWarningToast(`"${name}" already exists.`);
      return;
    }
    this.salesTaxOptions = [...this.salesTaxOptions, Object.assign(new salesTax(), { ...this.newTax, name })];
    this.newTax = { name: '', state_rate: 0, county_rate: 0, city_rate: 0, special_rate: 0 };
  }

  removeTaxRate(index: number) {
    this.salesTaxOptions = this.salesTaxOptions.filter((_, i) => i !== index);
  }

  constructor(
    private formBuilder: FormBuilder,
    public sharedService: SharedService,
    public accountingService: AccountingService,
  ) {
    this.accountingOptionsForm = this.formBuilder.group({
      costing_method_for_inventory: [null, Validators.required],
      cash_accrual_basis: [
        { value: 'Cash Basis', disabled: true },
        Validators.required,
      ],
      default_sales_receipt_message: ['Thank you for your purchase!', Validators.maxLength(255)],
      default_message_on_sales_statement: ['', Validators.maxLength(255)],
      credit_card_processing_rate: [0, [Validators.min(0), Validators.max(100)]],
    });
    this.accountingOptionsForm.get('costing_method_for_inventory').value;
  }

  ngOnInit() {
    this.populateForm();
    this.subscription = this.sharedService.company_info_retrieved$.subscribe(() => {
      this.populateForm();
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  populateForm() {
    const opts = this.sharedService.company_info?.accounting_options;
    if (opts) {
      this.accountingOptionsForm.patchValue({
        costing_method_for_inventory: opts.costing_method_for_inventory || null,
        cash_accrual_basis: opts.cash_accrual_basis || 'Cash Basis',
        default_sales_receipt_message: opts.default_sales_receipt_message || '',
        default_message_on_sales_statement: opts.default_message_on_sales_statement || '',
        credit_card_processing_rate: opts.credit_card_processing_rate !== undefined && opts.credit_card_processing_rate !== null ? opts.credit_card_processing_rate : 0,
      });
      // Lock the costing method once it has been saved
      if (opts.costing_method_for_inventory) {
        this.accountingOptionsForm.get('costing_method_for_inventory').disable({ emitEvent: false });
      }
      const customs = opts.custom_accounts ?? [];
      this.customIncomeAccounts = customs
        .filter((a: any) => a.type === 'Income')
        .map((a: any) => a.name);
      this.customExpenseAccounts = customs
        .filter((a: any) => a.type === 'Expense')
        .map((a: any) => a.name);
      this.salesTaxOptions = opts.sales_tax_options
        ? opts.sales_tax_options.map((t: any) => Object.assign(new salesTax(), t))
        : [];
      // Strip any saved entries that duplicate a built-in default so we don't
      // show them twice (once in the read-only defaults section, once in the
      // sortable customs list below).
      const paDefaults = this.accountingService.payment_account_defaults;
      const pmDefaults = this.accountingService.payment_method_defaults;
      this.paymentAccounts = (opts.payment_accounts ?? []).filter(
        (a: string) => !paDefaults.some((d) => d.toLowerCase() === a.toLowerCase()),
      );
      this.paymentMethods = (opts.payment_methods ?? []).filter(
        (m: string) => !pmDefaults.some((d) => d.toLowerCase() === m.toLowerCase()),
      );
    }
  }

  addCustomAccount() {
    const name = this.newAccountName?.trim();
    if (!name) return;
    const duplicate = [
      ...this.accountingService.income_expense_accounts,
      ...this.customAccounts,
    ].some((a) => a.type === this.newAccountType && a.name.toLowerCase() === name.toLowerCase());
    if (duplicate) {
      this.sharedService.raiseWarningToast(`"${name}" already exists as a ${this.newAccountType} account.`);
      return;
    }
    if (this.newAccountType === 'Income') {
      this.customIncomeAccounts = [...this.customIncomeAccounts, name];
    } else {
      this.customExpenseAccounts = [...this.customExpenseAccounts, name];
    }
    this.newAccountName = '';
  }

  // Remove by name, not index — see note above on bs-sortable item context.
  removeCustomIncomeAccount(name: string) {
    this.customIncomeAccounts = this.customIncomeAccounts.filter((n) => n !== name);
  }

  removeCustomExpenseAccount(name: string) {
    this.customExpenseAccounts = this.customExpenseAccounts.filter((n) => n !== name);
  }

  async onSubmit() {
    if (this.accountingOptionsForm.valid || this.accountingOptionsForm.getRawValue().costing_method_for_inventory) {
      this.sharedService.openStatusSpinner('Saving accounting options...');
      try {
        if (!this.sharedService.company_info.accounting_options) {
          this.sharedService.company_info.accounting_options = {} as any;
        }
        Object.assign(this.sharedService.company_info.accounting_options, {
          ...this.accountingOptionsForm.getRawValue(),
          custom_accounts: this.customAccounts,
          sales_tax_options: this.salesTaxOptions.map(t => ({
            name: t.name,
            state_rate: t.state_rate,
            county_rate: t.county_rate,
            city_rate: t.city_rate,
            special_rate: t.special_rate,
          })),
          payment_accounts: this.paymentAccounts,
          payment_methods: this.paymentMethods,
        });
        await this.sharedService.updateCompanyInformation(this.sharedService.company_info);
        // Lock costing method after first save
        this.accountingOptionsForm.get('costing_method_for_inventory').disable({ emitEvent: false });
        // Re-merge so the service list reflects the saved custom accounts immediately
        this.accountingService.mergeCustomAccounts();
      } catch (error) {
        console.error('Failed to save accounting options', error);
      } finally {
        this.sharedService.closeStatusSpinner();
      }
    }
  }
}
